import React, { useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { X, Send, Loader2, MapPin, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MELBOURNE_CENTER = [-37.8136, 144.9631];

const CATEGORIES = [
  { value: 'shooting',    label: '🔫 Shooting' },
  { value: 'firebombing', label: '🧨 Firebombing' },
  { value: 'arson',       label: '🔥 Arson' },
  { value: 'murder',      label: '💀 Murder' },
  { value: 'assault',     label: '👊 Assault' },
  { value: 'stabbing',    label: '🔪 Stabbing' },
  { value: 'kidnapping',  label: '🚗 Kidnapping' },
  { value: 'drug_bust',   label: '💊 Drug Bust' },
  { value: 'arrest',      label: '👮 Arrest' },
  { value: 'raid',        label: '🚨 Police Raid' },
  { value: 'robbery',     label: '🎭 Robbery' },
  { value: 'vehicle',     label: '🏎️ Vehicle' },
  { value: 'court',       label: '⚖️ Court' },
  { value: 'prison',      label: '🔒 Prison' },
  { value: 'gangland',    label: '⚔️ Gangland' },
  { value: 'other',       label: '📍 Other' },
];

const pickerIcon = L.divIcon({
  className: 'crime-marker-wrap',
  html: `<div class="crime-marker" style="--marker-color:#facc15">
           <div class="crime-marker__glow"></div>
           <div class="crime-marker__bubble"><span class="crime-marker__emoji">📍</span></div>
           <div class="crime-marker__stem"></div>
         </div>`,
  iconSize: [40, 52],
  iconAnchor: [20, 48],
});

// Handles map clicks: sets the picked lat/lng in parent state.
const ClickHandler = ({ onPick }) => {
  useMapEvents({
    click(e) { onPick([e.latlng.lat, e.latlng.lng]); },
  });
  return null;
};

const SubmitIncidentModal = ({ open, onClose }) => {
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [pos, setPos] = useState(null);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [category, setCategory] = useState('other');
  const [eventDate, setEventDate] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = useCallback(() => {
    setStep('form'); setPos(null); setTitle(''); setSummary('');
    setCategory('other'); setEventDate(''); setEmail(''); setError('');
  }, []);

  const handleClose = () => { onClose(); setTimeout(reset, 300); };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!pos) { setError('Tap the map to drop your pin first.'); return; }
    if (title.trim().length < 3) { setError('Give the incident a short title.'); return; }
    if (summary.trim().length < 10) { setError('Add at least 10 characters of context.'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/crime-map/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          summary: summary.trim(),
          lat: pos[0],
          lng: pos[1],
          category,
          event_date: eventDate || null,
          contact_email: email.trim() || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Submission failed. Try again.');
      setStep('success');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
      data-testid="submit-incident-modal"
      role="dialog"
    >
      <div className="bg-zinc-950 border border-red-900/50 rounded-xl shadow-[0_30px_80px_-20px_rgba(220,38,38,0.5)] w-full max-w-2xl my-6">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-2 min-w-0">
            <MapPin className="h-4 w-4 text-red-500 shrink-0" />
            <h3 className="text-sm font-black uppercase tracking-widest text-white truncate">
              Submit an Incident
            </h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            data-testid="submit-incident-close"
            className="text-gray-400 hover:text-white p-1"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {step === 'success' ? (
          <div className="px-5 py-10 text-center" data-testid="submit-incident-success">
            <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
            <p className="text-base font-black text-white mb-1">Submission received</p>
            <p className="text-sm text-gray-400 max-w-sm mx-auto">
              Our editors will review your pin. If it&apos;s verified, it&apos;ll appear on the
              public map. Thanks for helping us build the picture.
            </p>
            <button
              type="button"
              onClick={handleClose}
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded bg-red-600 hover:bg-red-500 text-white text-xs font-black uppercase tracking-widest"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-5 py-4 space-y-3">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">
                1. Tap the location on this map
              </label>
              <div
                className="rounded overflow-hidden border border-zinc-800"
                style={{ height: '240px' }}
                data-testid="submit-incident-map"
              >
                <MapContainer
                  center={MELBOURNE_CENTER}
                  zoom={11}
                  scrollWheelZoom={false}
                  style={{ height: '100%', width: '100%', background: '#000' }}
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap &copy; CARTO'
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    maxZoom={19}
                  />
                  <ClickHandler onPick={setPos} />
                  {pos && <Marker position={pos} icon={pickerIcon} />}
                </MapContainer>
              </div>
              <p className="text-[10px] text-gray-500 mt-1 font-mono">
                {pos
                  ? `Pinned: ${pos[0].toFixed(4)}, ${pos[1].toFixed(4)}`
                  : 'No pin dropped yet.'}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">
                  2. Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  data-testid="submit-incident-category"
                  className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded px-2.5 py-2 text-sm text-white outline-none"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">
                  Approximate date (optional)
                </label>
                <input
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  data-testid="submit-incident-date"
                  className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded px-2.5 py-2 text-sm text-white outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">
                3. Short title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={160}
                placeholder="e.g. Tobacco shop firebombed, Sydney Rd"
                data-testid="submit-incident-title"
                className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded px-2.5 py-2 text-sm text-white outline-none"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">
                4. What happened?
              </label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                maxLength={600}
                rows={4}
                required
                placeholder="Brief factual description. No names of victims or suspects unless already public."
                data-testid="submit-incident-summary"
                className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded px-2.5 py-2 text-sm text-white outline-none resize-y"
              />
              <p className="text-[10px] text-gray-500 text-right mt-0.5">{summary.length}/600</p>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">
                Contact email (optional - for us to follow up)
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={200}
                placeholder="you@example.com"
                data-testid="submit-incident-email"
                className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded px-2.5 py-2 text-sm text-white outline-none"
              />
            </div>

            {error && (
              <p className="text-xs text-red-400 font-bold" data-testid="submit-incident-error">
                {error}
              </p>
            )}

            <div className="flex items-center justify-between pt-1">
              <p className="text-[10px] text-gray-500 max-w-xs">
                By submitting you confirm the account is true to your knowledge.
                Editors verify every pin before publishing.
              </p>
              <button
                type="submit"
                disabled={busy}
                data-testid="submit-incident-submit"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-gray-500 text-white text-xs font-black uppercase tracking-widest transition-colors"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Submit
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SubmitIncidentModal;
